import React from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Box from "@mui/material/Box";
import { styled } from "@mui/material/styles";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";

const CustomDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiPaper-root": {
    borderRadius: "20px",
  },
}));

const CustomDialogTitle = styled(DialogTitle)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  gap: "10px",
  paddingLeft: theme.spacing(3),
  paddingRight: theme.spacing(3),
}));

const CustomDialogContent = styled(DialogContent)(({ theme }) => ({
  padding: theme.spacing(4),
}));

const VideoWrapper = styled(Box)({
  position: "relative",
  paddingTop: "56.25%",
});

interface Props {
  isOpen: boolean;
  url: string;
  onClose: () => void;
}

class VideoPreviewModal extends React.Component<Props> {
  scrollRef: number = 0;

  componentDidUpdate(prevProps: Readonly<Props>): void {
    const { isOpen } = this.props;
    if (prevProps.isOpen !== isOpen) {
      if (isOpen) {
        this.scrollRef = window.scrollY;
        document.body.style.position = "fixed";
        document.body.style.top = `-${this.scrollRef}px`;
        document.body.style.width = "100%";
      } else {
        this.enableScroll();
      }
    }
  }

  componentWillUnmount(): void {
    this.enableScroll();
  }

  enableScroll = () => {
    setTimeout(() => {
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.width = "";
      window.scrollTo(0, this.scrollRef);
    }, 300);
  };

  getVideoPlatform = (url: string) => {
    if (url.includes("youtube.com") || url.includes("youtu.be"))
      return "youtube";
    if (url.includes("vimeo.com")) return "vimeo";
    return null;
  };

  getVideoId = (url: string) => {
    const platform = this.getVideoPlatform(url);
    if (platform === "youtube") {
      const match = url.match(
        /^.*(?:youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/
      );
      return match && match[1].length === 11 ? match[1] : null;
    } else if (platform === "vimeo") {
      const match = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
      return match && match[1] ? match[1] : null;
    }
    return null;
  };

  getEmbedUrl = (url: string) => {
    const platform = this.getVideoPlatform(url);
    const videoId = this.getVideoId(url);

    if (!videoId) return null;

    if (platform === "youtube") {
      return `https://www.youtube.com/embed/${videoId}`;
    } else if (platform === "vimeo") {
      return `https://player.vimeo.com/video/${videoId}`;
    }

    return null;
  };

  render() {
    const { isOpen, onClose, url } = this.props;
    const embedUrl = this.getEmbedUrl(url);

    return (
      <CustomDialog open={isOpen} onClose={onClose} fullWidth maxWidth="lg">
        <CustomDialogTitle>
          <IconButton onClick={onClose}>
            <CloseRoundedIcon fontSize="small" />
          </IconButton>
        </CustomDialogTitle>
        <CustomDialogContent>
          {embedUrl ? (
            <VideoWrapper>
              <iframe
                src={embedUrl}
                title="Embedded Video"
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  border: "none",
                }}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </VideoWrapper>
          ) : (
            <Typography color="textSecondary">Unable to load video</Typography>
          )}
        </CustomDialogContent>
      </CustomDialog>
    );
  }
}

export default VideoPreviewModal;
